import { PopperPlacementType } from '@mui/material/Popper';

export interface GuidedTourStep {
  id: string;
  selector: string;
  titleKey: string;
  descriptionKey: string;
  title?: string;
  description?: string;
  titleOptions?: Record<string, string | number>;
  descriptionOptions?: Record<string, string | number>;
  placement?: PopperPlacementType;
}
// Pasos globales
export const globalGuidedTourSteps: GuidedTourStep[] = [
  {
    id: 'app-header',
    selector: '[data-tour="app-header"]',
    titleKey: 'guidedMode.steps.header.title',
    descriptionKey: 'guidedMode.steps.header.description',
    placement: 'bottom',
  },
  {
    id: 'guided-tour-button',
    selector: '[data-tour="guided-tour-button"]',
    titleKey: 'guidedMode.steps.tourButton.title',
    descriptionKey: 'guidedMode.steps.tourButton.description',
    placement: 'bottom-end',
  },
  {
    id: 'header-user-menu',
    selector: '[data-tour="header-user-menu"]',
    titleKey: 'guidedMode.steps.userMenu.title',
    descriptionKey: 'guidedMode.steps.userMenu.description',
    placement: 'bottom-end',
  },
  {
    id: 'sidebar-main-nav',
    selector: '[data-tour="sidebar-main-nav"]',
    titleKey: 'guidedMode.steps.sidebar.title',
    descriptionKey: 'guidedMode.steps.sidebar.description',
    placement: 'right',
  },
  {
    id: 'sidebar-search',
    selector: '[data-tour="sidebar-search"]',
    titleKey: 'guidedMode.steps.sidebarSearch.title',
    descriptionKey: 'guidedMode.steps.sidebarSearch.description',
    placement: 'right-start',
  },
];

// Pasos base para los reportes
export const reportBaseGuidedTourSteps: GuidedTourStep[] = [
  {
    id: 'report-filters',
    selector: '[data-tour="filter-panel"]',
    titleKey: 'guidedMode.steps.filters.title',
    descriptionKey: 'guidedMode.steps.filters.description',
    placement: 'bottom',
  },
  {
    id: 'report-filter-actions',
    selector: '[data-tour="report-filter-actions"]',
    titleKey: 'guidedMode.steps.filterActions.title',
    descriptionKey: 'guidedMode.steps.filterActions.description',
    placement: 'bottom-end',
  },
  {
    id: 'report-results',
    selector: '[data-tour="report-results"]',
    titleKey: 'guidedMode.steps.reportResults.title',
    descriptionKey: 'guidedMode.steps.reportResults.description',
    placement: 'top',
  },
  {
    id: 'report-grid-actions',
    selector: '[data-tour="report-grid-actions"]',
    titleKey: 'guidedMode.steps.reportGridActions.title',
    descriptionKey: 'guidedMode.steps.reportGridActions.description',
    placement: 'bottom-end',
  },
];

// Pasos base para reportes con acciones de proceso (Cancellations, Reprocess, OakBrook, Company Codes, Accounting Entries, Month Closing)
export const reportActionsBaseGuidedTourSteps: GuidedTourStep[] = [
  {
    id: 'report-filters',
    selector: '[data-tour="filter-panel"]',
    titleKey: 'guidedMode.steps.filters.title',
    descriptionKey: 'guidedMode.steps.filters.description',
    placement: 'bottom',
  },
  {
    id: 'report-results',
    selector: '[data-tour="report-results"]',
    titleKey: 'guidedMode.steps.reportResults.title',
    descriptionKey: 'guidedMode.steps.reportResults.description',
    placement: 'top',
  },
  {
    id: 'process-actions',
    selector: '[data-tour="process-actions"]',
    titleKey: 'guidedMode.steps.processActions.title',
    descriptionKey: 'guidedMode.steps.processActions.description',
    placement: 'bottom-end',
  },
  {
    id: 'report-grid-actions',
    selector: '[data-tour="report-grid-actions"]',
    titleKey: 'guidedMode.steps.reportGridActions.title',
    descriptionKey: 'guidedMode.steps.reportGridActions.description',
    placement: 'bottom-end',
  },
];

// Mapeo de pasos por ruta
export const guidedTourStepsByPath: Record<string, GuidedTourStep[]> = {
  '/dashboard': [
    {
      id: 'dashboard-controls',
      selector: '[data-tour="dashboard-controls"]',
      titleKey: 'guidedMode.steps.dashboardControls.title',
      descriptionKey: 'guidedMode.steps.dashboardControls.description',
      placement: 'bottom-end',
    },
    {
      id: 'dashboard-insights',
      selector: '[data-tour="dashboard-insights"]',
      titleKey: 'guidedMode.steps.dashboardInsights.title',
      descriptionKey: 'guidedMode.steps.dashboardInsights.description',
      placement: 'top',
    },
  ],
  '/sale-totals': reportBaseGuidedTourSteps,
  '/sales-product': reportBaseGuidedTourSteps,
  '/gcs-product': reportBaseGuidedTourSteps,
  '/third-party-sales-by-items': reportBaseGuidedTourSteps,
  '/report-tender': reportBaseGuidedTourSteps,
  '/reliefs': reportBaseGuidedTourSteps,
  '/gcs': reportBaseGuidedTourSteps,
  '/gcs-payment-types': reportBaseGuidedTourSteps,
  '/gcs-taxes': reportBaseGuidedTourSteps,
  '/petty-cash-difference': reportBaseGuidedTourSteps,
  '/petty-cash-difference-by-payment-type': reportBaseGuidedTourSteps,
  '/economic-accounting-report': reportBaseGuidedTourSteps,
  '/envelope-details-report': reportBaseGuidedTourSteps,
  '/sales-journal': reportBaseGuidedTourSteps,
  '/report-cash-movements': reportBaseGuidedTourSteps,
  '/petty-cash-withdrawal-report': reportBaseGuidedTourSteps,
  '/cash-count-report': reportBaseGuidedTourSteps,
  '/recovery-report': reportBaseGuidedTourSteps,
  '/exchange-purchase-report': reportBaseGuidedTourSteps,
  '/fixed-fund-report': reportBaseGuidedTourSteps,
  '/cash-discrepancy-report': reportBaseGuidedTourSteps,
  '/extra-cash-report': reportBaseGuidedTourSteps,
  '/missing-closing-detail': reportBaseGuidedTourSteps,
  '/cancellations': reportBaseGuidedTourSteps,
  '/masters-import': reportBaseGuidedTourSteps,
  '/company-codes': reportActionsBaseGuidedTourSteps,
  '/accounting-parameters': [
    {
      id: 'report-filters',
      selector: '[data-tour="filter-panel"]',
      titleKey: 'guidedMode.steps.filters.title',
      descriptionKey: 'guidedMode.steps.filters.description',
      placement: 'bottom',
    },
    {
      id: 'report-results',
      selector: '[data-tour="report-results"]',
      titleKey: 'guidedMode.steps.reportResults.title',
      descriptionKey: 'guidedMode.steps.reportResults.description',
      placement: 'top',
    },
    {
      id: 'process-actions',
      selector: '[data-tour="process-actions"]',
      titleKey: 'guidedMode.steps.processActions.title',
      descriptionKey: 'guidedMode.steps.processActions.description',
      placement: 'bottom-end',
    },
  ],
  '/accounting-entries': reportActionsBaseGuidedTourSteps,
  '/month-closing': reportActionsBaseGuidedTourSteps,
  '/oak-brook': reportActionsBaseGuidedTourSteps,
  '/reprocess': reportActionsBaseGuidedTourSteps,
};

export const getGuidedTourStepsForPath = (pathname: string): GuidedTourStep[] => {
  const matchedPath = Object.keys(guidedTourStepsByPath).find(
    path => pathname === path || pathname.startsWith(`${path}/`)
  );

  return matchedPath ? guidedTourStepsByPath[matchedPath] : [];
};
